// HRMS_ADMIN/src/views/hrms/employees/EmployeeSalary.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Loading from "components/common/Loading";
import {
  getEmployeeById,
  getEmployeePayslip,
} from "../../../services/hrms.api";

function fmtMoney(v) {
  const n = Number(v) || 0;
  return `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export default function EmployeeSalary() {
  const { id } = useParams(); // employee dbId
  const nav = useNavigate();

  const [loading, setLoading] = useState(true);
  const [emp, setEmp] = useState(null);
  const [payslip, setPayslip] = useState(null);
  const [error, setError] = useState("");

  // default: current month
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1–12

  function getPrevYm(year, month) {
    if (month === 1) return { year: year - 1, month: 12 };
    return { year, month: month - 1 };
  }

  useEffect(() => {
    let alive = true;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        // very important: clear old data when month/year changes
        setPayslip(null);
       

        const { year: prevYear, month: prevMonth } = getPrevYm(year, month);

        const [empRes, slipRes] = await Promise.allSettled([
          getEmployeeById(id),
          getEmployeePayslip(id, year, month),
          getEmployeePayslip(id, prevYear, prevMonth),
        ]);

        if (!alive) return;

        // employee always expected to succeed
        if (empRes.status === "fulfilled") {
          setEmp(empRes.value.data);
        } else {
          console.error("Failed to load employee:", empRes.reason);
          setError(
            empRes.reason?.response?.data?.message ||
              empRes.reason?.message ||
              "Failed to load employee details."
          );
        }

        // current month payslip
        if (slipRes.status === "fulfilled") {
          setPayslip(slipRes.value.data);
        } else if (
          slipRes.status === "rejected" &&
          slipRes.reason?.response?.status === 404
        ) {
          // no payslip generated for this month → leave payslip as null
          setPayslip(null);
          setError(
            slipRes.reason?.response?.data?.message ||
              "Payslip not found for that month. Payroll was not generated for this period."
          );
        } else if (slipRes.status === "rejected") {
          console.error("Failed to load payslip:", slipRes.reason);
          setPayslip(null);
          setError(
            slipRes.reason?.response?.data?.message ||
              slipRes.reason?.message ||
              "Failed to load payslip."
          );
        }
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setPayslip(null);
      
        setError(
          e?.response?.data?.message ||
            e.message ||
            "Failed to load salary details."
        );
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();

    return () => {
      alive = false;
    };
  }, [id, year, month]);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError("");
      setPayslip(null);

      const { data } = await getEmployeePayslip(id, year, month);
      setPayslip(data);
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 404) {
        setPayslip(null);
        setError(
          e?.response?.data?.message ||
            "Payslip not found for that month. Payroll was not generated for this period."
        );
      } else {
        setPayslip(null);
        setError(
          e?.response?.data?.message ||
            e.message ||
            "Failed to refresh payslip."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading variant="dots" fullscreen />;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => nav(-1)}
            className="flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <h2 className="text-2xl font-semibold dark:text-white">
            Salary Details
          </h2>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="rounded-lg border px-2 py-1 dark:border-white/10 dark:bg-navy-800 dark:text-white"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
              <option key={m} value={m}>
                {new Date(2000, m - 1, 1).toLocaleString("en-GB", {
                  month: "short",
                })}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value) || year)}
            className="w-20 rounded-lg border px-2 py-1 text-sm dark:border-white/10 dark:bg-navy-800 dark:text-white"
          />
          <button
            type="button"
            onClick={handleRefresh}
            className="rounded-lg bg-gradient-to-r from-yellow-500 to-red-500 px-3 py-1.5 text-sm text-white"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800/40 dark:bg-red-900/20 dark:text-red-100">
          {error}
        </div>
      )}

      {/* Employee header */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-white/10 dark:bg-navy-800">
        <h3 className="text-lg font-semibold dark:text-white">
          {emp?.firstName} {emp?.lastName} ({emp?.code})
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
          {emp?.designation || "Employee"} • {emp?.department || "General"}
        </p>
      </div>

      {/* Pay summary */}
      {payslip ? (
        <div className="grid gap-4 md:grid-cols-3">
          <SummaryCard
            label="Monthly CTC (30 days)"
            value={fmtMoney(payslip.monthlySalary)}
          />
          <SummaryCard
            label="Per Day Salary"
            value={fmtMoney(payslip.perDaySalary)}
          />
          <SummaryCard
            label="Net Payable"
            value={fmtMoney(payslip.netPayable)}
            highlight
          />

          <SummaryCard label="Present Days" value={payslip.presentDays} />
          <SummaryCard label="Half Days" value={payslip.halfDays} />
          <SummaryCard label="Absent Days" value={payslip.absentDays} />

          <SummaryCard label="Payable Days" value={payslip.payableDays} />
          <SummaryCard
            label="Total Hours"
            value={payslip.totalHours?.toFixed(1)}
          />
          <SummaryCard
            label="Total Deductions"
            value={fmtMoney(payslip.totalDeductions)}
          />
        </div>
      ) : (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-600/40 dark:bg-amber-900/20 dark:text-amber-100">
          No payslip found for this month. Run payroll generation first.
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-4 text-sm dark:border-white/10 ${
        highlight
          ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20"
          : "border-gray-200 bg-white dark:bg-navy-800"
      }`}
    >
      <div className="text-xs uppercase text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-2 text-lg font-semibold dark:text-white">{value}</div>
    </div>
  );
}
